import React, { useState, useEffect, useRef } from "react";

// Chakra imports
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Tag,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
  Input,
  useDisclosure,
} from "@chakra-ui/react";

// api calls
import { getClients } from "api/requests/getClients.js";
import { updateCrmAccount } from "api/requests/zohoRequests";

// Charts & Data
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register the components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

import { useLocation } from "react-router-dom";
import { archiveBudget } from "api/requests/archiveBudget";
import useAuth from "hooks/useAuth";

import ScheduleModal from "./components/ScheduleModal";

function BudgetEditor() {
  const toast = useToast();
  const { auth } = useAuth();
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "#4A5058");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [value, setValue] = useState();
  const { colorMode } = useColorMode();
  const [clients, setClients] = useState([]);
  const borderHeadColor = useColorModeValue("white", "transparent");
  const location = useLocation();
  const { client } = location.state || {};
  const [clientData, setClientData] = useState(client || {});
  const [originalData, setOriginalData] = useState(client || {});

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [brValue, setBrValue] = useState("");
  const [brPercent, setBrPercent] = useState("");

  // Colors
  const search = "yellow.500";
  const social = "blue.500";
  const ott = "red.500";

  // Handle input change
  const handleInputChange = (e) => {
    setIsDisabled(false);
    const { name, value } = e.target;

    const formatResult = (num) => {
      return num % 1 === 0 ? num : num.toFixed(2).replace(/\.00$/, "");
    };

    setClientData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      const parseValue = (val) => {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? 0 : parsed;
      };

      const sem = parseValue(newData.SEM);
      const sem_b_r1 = parseValue(newData.SEM_B_R1);
      const sem_new = parseValue(newData.SEM_New);
      const sem_used = parseValue(newData.SEM_Used);

      const spanish_search = parseValue(newData.Spanish_Search);
      const spanish_sem_b_r1 = parseValue(newData.Spanish_SEM_B_R1);
      const spanish_split_new = parseValue(newData.Spanish_Split_New);
      const spanish_split_used = parseValue(newData.Spanish_Split_Used);

      const vla = parseValue(newData.VLA);
      const vla_split_new = parseValue(newData.VLA_Split_New);
      const vla_split_used = parseValue(newData.VLA_Split_Used);

      const social = parseValue(newData.Social);
      const social_split_new = parseValue(newData.Social_Split_New);
      const social_split_used = parseValue(newData.Social_Split_Used);
      const spanish_social = parseValue(newData.Spanish_Social);
      const social_spanish_split_new = parseValue(
        newData.Social_Spanish_Split_New
      );
      const social_spanish_split_used = parseValue(
        newData.Social_Spanish_Split_Used
      );

      const calculateSEM = () => {
        const intermediate1 = isNaN((100 - sem_b_r1) / 100)
          ? 0
          : (100 - sem_b_r1) / 100;
        const intermediate2 = isNaN(sem * intermediate1)
          ? 0
          : sem * intermediate1;
        return {
          SEM_New1: isNaN(intermediate2 * sem_new * 0.01)
            ? 0
            : formatResult(intermediate2 * sem_new * 0.01),
          SEM_Used1: isNaN(intermediate2 * sem_used * 0.01)
            ? 0
            : formatResult(intermediate2 * sem_used * 0.01),
          SEM_B_R: isNaN(sem * sem_b_r1 * 0.01)
            ? 0
            : formatResult(sem * sem_b_r1 * 0.01),
        };
      };

      const calculateSpanishSEM = () => {
        const intermediate1 = isNaN((100 - spanish_sem_b_r1) / 100)
          ? 0
          : (100 - spanish_sem_b_r1) / 100;
        const intermediate2 = isNaN(spanish_search * intermediate1)
          ? 0
          : spanish_search * intermediate1;

        return {
          Spanish_SEM_New: isNaN(intermediate2 * spanish_split_new * 0.01)
            ? 0
            : formatResult(intermediate2 * spanish_split_new * 0.01),
          Spanish_SEM_Used: isNaN(intermediate2 * spanish_split_used * 0.01)
            ? 0
            : formatResult(intermediate2 * spanish_split_used * 0.01),
          Spanish_SEM_B_R: isNaN(spanish_search * spanish_sem_b_r1 * 0.01)
            ? 0
            : formatResult(spanish_search * spanish_sem_b_r1 * 0.01),
        };
      };

      const calculateVLA = () => {
        return {
          VLA_New: isNaN(vla * vla_split_new * 0.01)
            ? 0
            : formatResult(vla * vla_split_new * 0.01),
          VLA_Used: isNaN(vla * vla_split_used * 0.01)
            ? 0
            : formatResult(vla * vla_split_used * 0.01),
        };
      };

      const calculateSocial = () => {
        return {
          Social_New: isNaN(social * social_split_new * 0.01)
            ? 0
            : formatResult(social * social_split_new * 0.01),
          Social_Used: isNaN(social * social_split_used * 0.01)
            ? 0
            : formatResult(social * social_split_used * 0.01),
          Social_Spanish_New: isNaN(
            spanish_social * social_spanish_split_new * 0.01
          )
            ? 0
            : formatResult(spanish_social * social_spanish_split_new * 0.01),
          Social_Spanish_Used: isNaN(
            spanish_social * social_spanish_split_used * 0.01
          )
            ? 0
            : formatResult(spanish_social * social_spanish_split_used * 0.01),
        };
      };

      // Calculate SEM fields
      const { SEM_New1, SEM_Used1, SEM_B_R } = calculateSEM();
      newData.SEM_New1 = SEM_New1;
      newData.SEM_Used1 = SEM_Used1;
      newData.SEM_B_R = SEM_B_R;

      // Calculate Spanish SEM fields
      const {
        Spanish_SEM_New,
        Spanish_SEM_Used,
        Spanish_SEM_B_R,
      } = calculateSpanishSEM();
      newData.Spanish_SEM_New = Spanish_SEM_New;
      newData.Spanish_SEM_Used = Spanish_SEM_Used;
      newData.Spanish_SEM_B_R = Spanish_SEM_B_R;

      // Calculate VLA fields
      const { VLA_New, VLA_Used } = calculateVLA();
      newData.VLA_New = VLA_New;
      newData.VLA_Used = VLA_Used;

      // Calculate Social fields
      const {
        Social_New,
        Social_Used,
        Social_Spanish_New,
        Social_Spanish_Used,
      } = calculateSocial();
      newData.Social_New = Social_New;
      newData.Social_Used = Social_Used;
      newData.Social_Spanish_New = Social_Spanish_New;
      newData.Social_Spanish_Used = Social_Spanish_Used;

      return newData;
    });
  };

  const fetchClients = async () => {
    try {
      const data = await getClients();
      setClients((prev) => data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    setClientData(client || {});
    setOriginalData(client || {});
  }, [client]);

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: colorMode === "dark" ? "#1b254b" : "white",
      padding: "4px",
      borderRadius: "7px",
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor:
        isSelected && colorMode === "dark"
          ? "#3182ce"
          : isFocused
          ? "#3182ce"
          : undefined,
      color: colorMode === "dark" ? "white" : isFocused ? "white" : "black",
    }),
    singleValue: (styles, { isFocused }) => {
      return {
        ...styles,
        color: chroma("#fff").hex(),
        backgroundColor:
          colorMode === "dark" ? "#3182ce" : isFocused ? "#d0dcfb" : "#3182ce",
        padding: "6px",
        borderRadius: "5px",
        fontSize: "85%",
        width: "fit-content",
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: colorMode === "dark" ? "#1b254b" : "white",
    }),
  };

  function safeNumber(val) {
    return isNaN(val) ? 0 : val;
  }

  const saveChanges = async () => {
    if (!clientData || !originalData) {
      console.error("clientData or originalData is undefined.");
      setIsLoading(false);
      setIsDisabled(false);
      return;
    }
    const updatedFields = {
      id: client?.id,
    };
    Object.keys(clientData).forEach((key) => {
      if (clientData[key] !== originalData[key]) {
        updatedFields[key] = clientData[key];
      }
    });
    console.log(updatedFields);
    setIsLoading(false);
    setIsDisabled(true);
    const response = await updateCrmAccount(updatedFields);
    const status = response?.data?.[0]?.status;
    if (status === "success") {
      toast({
        title: "Success!",
        description: "Client updates saved",
        status: "success",
        duration: 2000,
      });
      const clientId = clientData.id;
      const data = [
        {
          Overall_Total_Budget:
            (parseFloat(clientData.SEM) || parseFloat(client?.SEM) || 0) +
            (parseFloat(clientData?.Fixed_Ops_Search) ||
              parseFloat(client?.Fixed_Ops_Search) ||
              0) +
            (parseFloat(clientData?.Spanish_Search) ||
              parseFloat(client?.Spanish_Search) ||
              0) +
            (parseFloat(clientData?.VLA) || parseFloat(client?.VLA) || 0) +
            (parseFloat(clientData?.Misc_Spend_1) ||
              parseFloat(client?.Misc_Spend_1) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_2) ||
              parseFloat(client?.Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_3) ||
              parseFloat(client?.Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_4) ||
              parseFloat(client?.Misc_Spend_4) ||
              0) +
            (parseFloat(clientData?.Social) ||
              parseFloat(client?.Social) ||
              0) +
            (parseFloat(clientData?.Spanish_Social) ||
              parseFloat(client?.Spanish_Social) ||
              0) +
            (parseFloat(clientData?.Fixed_Ops_Social) ||
              parseFloat(client?.Fixed_Ops_Social) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_1) ||
              parseFloat(client?.S_Misc_Spend_1) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_2) ||
              parseFloat(client?.S_Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_3) ||
              parseFloat(client?.S_Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_4) ||
              parseFloat(client?.S_Misc_Spend_4) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_5) ||
              parseFloat(client?.S_Misc_Spend_5) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_6) ||
              parseFloat(client?.S_Misc_Spend_6) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_7) ||
              parseFloat(client?.S_Misc_Spend_7) ||
              0),
          search: [
            {
              SEM: safeNumber(clientData.SEM),
              SEM_New: safeNumber(clientData.SEM_New),
              SEM_New1: safeNumber(clientData.SEM_New1),
              SEM_Used: safeNumber(clientData.SEM_Used),
              SEM_Used1: safeNumber(clientData.SEM_Used1),
              Spanish_Search: safeNumber(clientData.Spanish_Search),
              Spanish_Split_New: safeNumber(clientData.Spanish_Split_New),
              Spanish_Split_Used: safeNumber(clientData.Spanish_Split_Used),
              Spanish_SEM_New: safeNumber(clientData.Spanish_SEM_New),
              Spanish_SEM_Used: safeNumber(clientData.Spanish_SEM_Used),
              SEM_B_R: safeNumber(clientData.SEM_B_R),
              SEM_B_R1: safeNumber(clientData.SEM_B_R1),
              Spanish_SEM_B_R1: safeNumber(clientData.Spanish_SEM_B_R1),
              Spanish_SEM_B_R: safeNumber(clientData.Spanish_SEM_B_R),
              VLA: safeNumber(clientData.VLA),
              VLA_Split_New: safeNumber(clientData.VLA_Split_New),
              VLA_Split_Used: safeNumber(clientData.VLA_Split_Used),
              VLA_New: safeNumber(clientData.VLA_New),
              VLA_Used: safeNumber(clientData.VLA_Used),
              Fixed_Ops_Search: safeNumber(clientData.Fixed_Ops_Search),
              Description_1: clientData.Description_1 || "",
              Description_2: clientData.Description_2 || "",
              Description_3: clientData.Description_3 || "",
              Description_4: clientData.Description_4 || "",
              Misc_Spend_1: clientData.Misc_Spend_1 || "",
              Misc_Spend_2: clientData.Misc_Spend_2 || "",
              Misc_Spend_3: clientData.Misc_Spend_3 || "",
              Misc_Spend_4: clientData.Misc_Spend_4 || "",
              Search_Total_Budget:
                (parseFloat(clientData.SEM) || parseFloat(client?.SEM) || 0) +
                (parseFloat(clientData.Fixed_Ops_Search) ||
                  parseFloat(client?.Fixed_Ops_Search) ||
                  0) +
                (parseFloat(clientData.Spanish_Search) ||
                  parseFloat(client?.Spanish_Search) ||
                  0) +
                (parseFloat(clientData.VLA) || parseFloat(client?.VLA) || 0) +
                (parseFloat(clientData.Misc_Spend_1) ||
                  parseFloat(client?.Misc_Spend_1) ||
                  0) +
                (parseFloat(clientData.Misc_Spend_2) ||
                  parseFloat(client?.Misc_Spend_2) ||
                  0) +
                (parseFloat(clientData.Misc_Spend_3) ||
                  parseFloat(client?.Misc_Spend_3) ||
                  0) +
                (parseFloat(clientData.Misc_Spend_4) ||
                  parseFloat(client?.Misc_Spend_4) ||
                  0),
            },
          ],
          social: [
            {
              Social: safeNumber(clientData.Social),
              Social_Split_New: safeNumber(clientData.Social_Split_New),
              Social_Split_Used: safeNumber(clientData.Social_Split_Used),
              Social_New: safeNumber(clientData.Social_New),
              Social_Used: safeNumber(clientData.Social_Used),
              Spanish_Social: safeNumber(clientData.Spanish_Social),
              Social_Spanish_Split_New: safeNumber(
                clientData.Social_Spanish_Split_New
              ),
              Social_Spanish_Split_Used: safeNumber(
                clientData.Social_Spanish_Split_Used
              ),
              Social_Spanish_New: safeNumber(clientData.Social_Spanish_New),
              Social_Spanish_Used: safeNumber(clientData.Social_Spanish_Used),
              Fixed_Ops_Social: safeNumber(clientData.Fixed_Ops_Social),
              S_Description_1: clientData.S_Description_1,
              S_Description_2: clientData.S_Description_2,
              S_Description_3: clientData.S_Description_3,
              S_Description_4: clientData.S_Description_4,
              S_Description_5: clientData.S_Description_5,
              S_Description_6: clientData.S_Description_6,
              S_Description_7: clientData.S_Description_7,
              S_Misc_Spend_1: clientData.S_Misc_Spend_1,
              S_Misc_Spend_2: clientData.S_Misc_Spend_2,
              S_Misc_Spend_3: clientData.S_Misc_Spend_3,
              S_Misc_Spend_4: clientData.S_Misc_Spend_4,
              S_Misc_Spend_5: clientData.S_Misc_Spend_5,
              S_Misc_Spend_6: clientData.S_Misc_Spend_6,
              S_Misc_Spend_7: clientData.S_Misc_Spend_7,
              Social_Total_Budget:
                (parseFloat(clientData.Social) ||
                  parseFloat(client?.Social) ||
                  0) +
                (parseFloat(clientData?.Spanish_Social) ||
                  parseFloat(client?.Spanish_Social) ||
                  0) +
                (parseFloat(clientData?.Fixed_Ops_Social) ||
                  parseFloat(client?.Fixed_Ops_Social) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_1) ||
                  parseFloat(client?.S_Misc_Spend_1) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_2) ||
                  parseFloat(client?.S_Misc_Spend_2) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_3) ||
                  parseFloat(client?.S_Misc_Spend_3) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_4) ||
                  parseFloat(client?.S_Misc_Spend_4) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_5) ||
                  parseFloat(client?.S_Misc_Spend_5) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_6) ||
                  parseFloat(client?.S_Misc_Spend_6) ||
                  0) +
                (parseFloat(clientData?.S_Misc_Spend_7) ||
                  parseFloat(client?.S_Misc_Spend_7) ||
                  0),
            },
          ],
          ott: [
            {
              OTT: safeNumber(clientData.OTT),
              Spanish_OTT: safeNumber(clientData.Spanish_OTT),
              OTT_Total_Budget:
                (parseFloat(clientData.OTT) || parseFloat(client?.OTT)) +
                (parseFloat(clientData.Spanish_OTT) ||
                  parseFloat(client?.Spanish_OTT)),
            },
          ],
        },
      ];
      const archive = await archiveBudget({ clientId, data });
    } else {
      toast({
        title: "Error!",
        description: `Status code: ${status}`,
        status: "error",
        duration: 2000,
      });
    }
  };

  const TotalBarChartData = [
    {
      id: "Search",
      label: "Search",
      value: client
        ? (
            (parseFloat(clientData.SEM) || parseFloat(client?.SEM) || 0) +
            (parseFloat(clientData?.Fixed_Ops_Search) ||
              parseFloat(client?.Fixed_Ops_Search) ||
              0) +
            (parseFloat(clientData?.Spanish_Search) ||
              parseFloat(client?.Spanish_Search) ||
              0) +
            (parseFloat(clientData?.VLA) || parseFloat(client?.VLA) || 0) +
            (parseFloat(clientData?.Misc_Spend_1) ||
              parseFloat(client?.Misc_Spend_1) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_2) ||
              parseFloat(client?.Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_3) ||
              parseFloat(client?.Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_4) ||
              parseFloat(client?.Misc_Spend_4) ||
              0)
          ).toString()
        : "0",
      color: "#fbbc04",
    },
    {
      id: "Social",
      label: "Social",
      value: client
        ? (
            (parseFloat(clientData.Social) || parseFloat(client?.Social) || 0) +
            (parseFloat(clientData?.Spanish_Social) ||
              parseFloat(client?.Spanish_Social) ||
              0) +
            (parseFloat(clientData?.Fixed_Ops_Social) ||
              parseFloat(client?.Fixed_Ops_Social) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_1) ||
              parseFloat(client?.S_Misc_Spend_1) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_2) ||
              parseFloat(client?.S_Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_3) ||
              parseFloat(client?.S_Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_4) ||
              parseFloat(client?.S_Misc_Spend_4) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_5) ||
              parseFloat(client?.S_Misc_Spend_5) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_6) ||
              parseFloat(client?.S_Misc_Spend_6) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_7) ||
              parseFloat(client?.S_Misc_Spend_7) ||
              0)
          ).toString()
        : "0",
      color: "#4a86e8",
    },
    {
      id: "OTT",
      label: "OTT",
      value: client
        ? parseFloat(clientData.OTT) || parseFloat(client?.OTT) || 0
        : "0",
      color: "red",
    },
  ];

  const SearchBarChartData = [
    {
      id: "SEM",
      label: "SEM",
      value: client
        ? parseFloat(clientData.SEM) || parseFloat(client?.SEM) || 0
        : 0,
      color: "#FFD700", // Gold
    },
    {
      id: "Spanish",
      label: "Spanish",
      value: client
        ? parseFloat(clientData.Spanish_Search) ||
          parseFloat(client?.Spanish_Search) ||
          0
        : 0,
      color: "#FFD700", // Light Pink
    },
    {
      id: "VLA",
      label: "VLA",
      value: client
        ? parseFloat(clientData.VLA) || parseFloat(client?.VLA) || 0
        : 0,
      color: "#FFD700", // Light Sky Blue
    },
    {
      id: "Fixed Ops",
      label: "Fixed Ops",
      value: client
        ? parseFloat(clientData.Fixed_Ops_Search) ||
          parseFloat(client?.Fixed_Ops_Search) ||
          0
        : 0,
      color: "#FFD700", // Light Green
    },
    {
      id: "Misc",
      label: "Misc",
      value: client
        ? (parseFloat(clientData?.Misc_Spend_1) ||
            parseFloat(client?.Misc_Spend_1) ||
            0) +
            (parseFloat(clientData?.Misc_Spend_2) ||
              parseFloat(client?.Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_3) ||
              parseFloat(client?.Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.Misc_Spend_4) ||
              parseFloat(client?.Misc_Spend_4) ||
              0) || 0
        : 0,
      color: "#FFD700", // Light Grey
    },
  ];

  const SocialBarChartData = [
    {
      id: "Social",
      label: "Social",
      value: client
        ? parseFloat(clientData.Social) || parseFloat(client?.Social) || 0
        : "0",
      color: "#4a86e8",
    },
    {
      id: "Spanish",
      label: "Spanish",
      value: client
        ? parseFloat(clientData.Spanish_Social) ||
          parseFloat(client?.Spanish_Social) ||
          0
        : "0",
      color: "#4a86e8",
    },
    {
      id: "Fixed Ops",
      label: "Fixed Ops",
      value: client
        ? parseFloat(clientData.Fixed_Ops_Social) ||
          parseFloat(client?.Fixed_Ops_Social) ||
          0
        : "0",
      color: "#4a86e8",
    },
    {
      id: "Misc",
      label: "Misc",
      value: client
        ? (parseFloat(clientData?.S_Misc_Spend_1) ||
            parseFloat(client?.S_Misc_Spend_1) ||
            0) +
            (parseFloat(clientData?.S_Misc_Spend_2) ||
              parseFloat(client?.S_Misc_Spend_2) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_3) ||
              parseFloat(client?.S_Misc_Spend_3) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_4) ||
              parseFloat(client?.S_Misc_Spend_4) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_5) ||
              parseFloat(client?.S_Misc_Spend_5) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_6) ||
              parseFloat(client?.S_Misc_Spend_6) ||
              0) +
            (parseFloat(clientData?.S_Misc_Spend_7) ||
              parseFloat(client?.S_Misc_Spend_7) ||
              0) || 0
        : "0",
      color: "#4a86e8",
    },
  ];

  const createChartData = (data) => {
    return {
      labels: data.map((item) => item.label),
      datasets: [
        {
          data: data.map((item) => parseFloat(item.value)),
          backgroundColor: data.map((item) => item.color),
          barThickness: "flex",
        },
      ],
    };
  };

  const createOptions = (title, textColor) => {
    return {
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: "Categories",
            color: textColor, // Set title text color
          },
          ticks: {
            display: true,
            color: textColor, // Set x-axis ticks color
          },
        },
        y: {
          title: {
            display: true,
            text: "Value",
            color: textColor, // Set title text color
          },
          ticks: {
            display: true,
            color: textColor, // Set y-axis ticks color
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          titleColor: "#ffffff", // Hover text color
          bodyColor: "#ffffff", // Hover text color
          footerColor: "#ffffff", // Hover text color
        },
        title: {
          display: true,
          text: title,
          color: textColor, // Set chart title color
        },
      },
    };
  };

  const chartRefs = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      chartRefs.current.forEach((chartRef) => {
        if (chartRef) {
          chartRef.resize();
        }
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Flex direction="column" pt={{ base: "150px", lg: "75px" }}>
      <Card bg={bgColor}>
        <Flex justify={"space-between"} alignItems={"center"}>
          <Flex direction={"column"}>
            <Heading
              p={3}
              size={{ sm: "sm", md: "sm", lg: "sm", xl: "md", "2xl": "lg" }}
            >
              {client
                ? client.Account_Name
                : "No Client Selected - Please select a client on the Budget Overview."}
            </Heading>
            <Text
              ml={3}
              pb={4}
              fontSize={{ sm: "sm", md: "sm", lg: "sm", xl: "sm", "2xl": "lg" }}
            >
              Total Combined Budgets:{" "}
              <Tag variant={"solid"} colorScheme="linkedin">
                $
                {client
                  ? (
                      (parseFloat(clientData.SEM) ||
                        parseFloat(client?.SEM) ||
                        0) +
                      (parseFloat(clientData?.Fixed_Ops_Search) ||
                        parseFloat(client?.Fixed_Ops_Search) ||
                        0) +
                      (parseFloat(clientData?.Spanish_Search) ||
                        parseFloat(client?.Spanish_Search) ||
                        0) +
                      (parseFloat(clientData?.VLA) ||
                        parseFloat(client?.VLA) ||
                        0) +
                      (parseFloat(clientData?.Misc_Spend_1) ||
                        parseFloat(client?.Misc_Spend_1) ||
                        0) +
                      (parseFloat(clientData?.Misc_Spend_2) ||
                        parseFloat(client?.Misc_Spend_2) ||
                        0) +
                      (parseFloat(clientData?.Misc_Spend_3) ||
                        parseFloat(client?.Misc_Spend_3) ||
                        0) +
                      (parseFloat(clientData?.Misc_Spend_4) ||
                        parseFloat(client?.Misc_Spend_4) ||
                        0) +
                      (parseFloat(clientData?.Social) ||
                        parseFloat(client?.Social) ||
                        0) +
                      (parseFloat(clientData?.Spanish_Social) ||
                        parseFloat(client?.Spanish_Social) ||
                        0) +
                      (parseFloat(clientData?.Fixed_Ops_Social) ||
                        parseFloat(client?.Fixed_Ops_Social) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_1) ||
                        parseFloat(client?.S_Misc_Spend_1) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_2) ||
                        parseFloat(client?.S_Misc_Spend_2) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_3) ||
                        parseFloat(client?.S_Misc_Spend_3) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_4) ||
                        parseFloat(client?.S_Misc_Spend_4) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_5) ||
                        parseFloat(client?.S_Misc_Spend_5) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_6) ||
                        parseFloat(client?.S_Misc_Spend_6) ||
                        0) +
                      (parseFloat(clientData?.S_Misc_Spend_7) ||
                        parseFloat(client?.S_Misc_Spend_7) ||
                        0) +
                      (parseFloat(clientData?.OTT) ||
                        parseFloat(client?.OTT) ||
                        0) +
                      (parseFloat(clientData?.Spanish_OTT) ||
                        parseFloat(client?.Spanish_OTT) ||
                        0)
                    ).toLocaleString()
                  : "0"}
              </Tag>
            </Text>
          </Flex>
          <Box>
            <Button
              variant={"solid"}
              bg={"yellow.400"}
              w={"130px"}
              mr={3}
              onClick={onOpen}
              isDisabled={client ? false : true}
              fontSize={{ sm: "sm", md: "sm", lg: "sm", xl: "xs", "2xl": "md" }}
            >
              Schedule
            </Button>
            <Button
              variant={"solid"}
              bg={"red"}
              isLoading={isLoading}
              isDisabled={isDisabled}
              loadingText="Submitting"
              w={"150px"}
              mr={3}
              onClick={() => saveChanges()}
              fontSize={{ sm: "sm", md: "sm", lg: "sm", xl: "xs", "2xl": "md" }}
            >
              Save Changes
            </Button>
          </Box>
        </Flex>
      </Card>
      {/* Charts */}
      <Flex
        direction={{
          sm: "column",
          md: "column",
          lg: "row",
          xl: "row",
          "2xl": "row",
        }}
        gap={2}
        pt={2}
        width={"100%"}
      >
        <Card p={2} bg={bgColor} w={"100%"}>
          <Box height="400px">
            <Bar
              ref={(el) => (chartRefs.current[0] = el)}
              data={createChartData(TotalBarChartData)}
              options={{
                ...createOptions("Over-all", textColor),
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </Box>
        </Card>
        <Card p={2} bg={bgColor} w={"100%"}>
          <Box height="400px">
            <Bar
              ref={(el) => (chartRefs.current[1] = el)}
              data={createChartData(SearchBarChartData)}
              options={{
                ...createOptions("Search", textColor),
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </Box>
        </Card>
        <Card p={2} bg={bgColor} w={"100%"}>
          <Box height="400px">
            <Bar
              ref={(el) => (chartRefs.current[2] = el)}
              data={createChartData(SocialBarChartData)}
              options={{
                ...createOptions("Social", textColor),
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </Box>
        </Card>
      </Flex>
      <Card mt={2} boxShadow={"sm"} bg={bgColor} p={5}>
        <Heading as={"h2"} size={"lg"} py={3}>
          Search
        </Heading>
        <Flex gap={1} pl={3}>
          <Text color={search} as={"b"}>
            Search Total Budget:
          </Text>
          <Tag variant={"solid"} colorScheme="linkedin">
            $
            {(
              (parseFloat(clientData.SEM) || parseFloat(client?.SEM) || 0) +
              (parseFloat(clientData.Fixed_Ops_Search) ||
                parseFloat(client?.Fixed_Ops_Search) ||
                0) +
              (parseFloat(clientData.Spanish_Search) ||
                parseFloat(client?.Spanish_Search) ||
                0) +
              (parseFloat(clientData.VLA) || parseFloat(client?.VLA) || 0) +
              (parseFloat(clientData.Misc_Spend_1) ||
                parseFloat(client?.Misc_Spend_1) ||
                0) +
              (parseFloat(clientData.Misc_Spend_2) ||
                parseFloat(client?.Misc_Spend_2) ||
                0) +
              (parseFloat(clientData.Misc_Spend_3) ||
                parseFloat(client?.Misc_Spend_3) ||
                0) +
              (parseFloat(clientData.Misc_Spend_4) ||
                parseFloat(client?.Misc_Spend_4) ||
                0)
            ).toLocaleString()}
          </Tag>
        </Flex>
        <Flex p={3} gap={5}>
          {/* SEM */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM:{" "}
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="SEM"
                value={clientData.SEM || "0"}
                onChange={handleInputChange}
              />
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM Split New:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="SEM_New"
                value={clientData.SEM_New || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM Split Used:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="SEM_Used"
                value={clientData.SEM_Used || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM New:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="SEM_New1"
                value={
                  clientData.SEM_New1 !== undefined ? clientData.SEM_New1 : "0"
                }
                readOnly
              />
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM Used:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="SEM_Used1"
                value={
                  clientData.SEM_Used1 !== undefined
                    ? clientData.SEM_Used1
                    : "0"
                }
                readOnly
              />
            </Flex>
          </Flex>
          {/* Spanish */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM:{" "}
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_Search"
                value={clientData.Spanish_Search || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM Split New:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Spanish_Split_New"
                value={clientData.Spanish_Split_New || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM Split Used:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Spanish_Split_Used"
                value={clientData.Spanish_Split_Used || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM New:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_SEM_New"
                value={
                  clientData.Spanish_SEM_New !== undefined
                    ? clientData.Spanish_SEM_New
                    : "0"
                }
                readOnly
              />
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM Used:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_SEM_Used"
                value={
                  clientData.Spanish_SEM_Used !== undefined
                    ? clientData.Spanish_SEM_Used
                    : "0"
                }
                readOnly
              />
            </Flex>
          </Flex>
          {/* VLA */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                VLA:{" "}
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="VLA"
                value={clientData.VLA || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                VLA Split New:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="VLA_Split_New"
                value={clientData.VLA_Split_New || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                VLA Split Used:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="VLA_Split_Used"
                value={clientData.VLA_Split_Used || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                VLA New:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="VLA_New"
                value={
                  clientData.VLA_New !== undefined ? clientData.VLA_New : "0"
                }
                readOnly
              />
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                VLA Used:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="VLA_Used"
                value={
                  clientData.VLA_Used !== undefined ? clientData.VLA_Used : "0"
                }
                readOnly
              />
            </Flex>
          </Flex>
          {/* B/R */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM B/R %:{" "}
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="SEM_B_R1"
                value={clientData.SEM_B_R1 || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                SEM B/R:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="SEM_B_R"
                value={
                  clientData.SEM_B_R !== undefined ? clientData.SEM_B_R : "0"
                }
                readOnly
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM B/R %:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Spanish_SEM_B_R1"
                value={clientData.Spanish_SEM_B_R1 || "0"}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Spanish SEM B/R:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_SEM_B_R"
                value={
                  clientData.Spanish_SEM_B_R !== undefined
                    ? clientData.Spanish_SEM_B_R
                    : "0"
                }
                readOnly
              ></Input>
            </Flex>
            {/* Fixed Ops */}
            <Flex direction={"column"} gap={1}>
              <Flex gap={1}>
                <Text color={search} as={"b"}>
                  Fixed Ops:
                </Text>
                <Text>$</Text>
                <Input
                  size={"xs"}
                  w={"60px"}
                  name="Fixed_Ops_Search"
                  value={clientData.Fixed_Ops_Search || "0"}
                  onChange={handleInputChange}
                ></Input>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Flex p={3} gap={5}>
          {/* Misc */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Misc 1:{" "}
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="Description_1"
                value={clientData?.Description_1 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Misc_Spend_1"
                value={clientData?.Misc_Spend_1 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Misc 2:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="Description_2"
                value={clientData?.Description_2 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Misc_Spend_2"
                value={clientData?.Misc_Spend_2 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Misc 3:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="Description_3"
                value={clientData?.Description_3 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Misc_Spend_3"
                value={clientData?.Misc_Spend_3 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={search} as={"b"}>
                Misc 4:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="Description_4"
                value={clientData?.Description_4 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Misc_Spend_4"
                value={clientData?.Misc_Spend_4 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
          </Flex>
        </Flex>
        <Heading as={"h2"} size={"lg"} py={3}>
          Social
        </Heading>
        <Flex gap={1} pl={3}>
          <Text color={social} as={"b"}>
            Social Total Budget:{" "}
          </Text>
          <Tag variant={"solid"} colorScheme="linkedin">
            $
            {(
              (parseFloat(clientData.Social) ||
                parseFloat(client?.Social) ||
                0) +
              (parseFloat(clientData?.Spanish_Social) ||
                parseFloat(client?.Spanish_Social) ||
                0) +
              (parseFloat(clientData?.Fixed_Ops_Social) ||
                parseFloat(client?.Fixed_Ops_Social) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_1) ||
                parseFloat(client?.S_Misc_Spend_1) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_2) ||
                parseFloat(client?.S_Misc_Spend_2) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_3) ||
                parseFloat(client?.S_Misc_Spend_3) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_4) ||
                parseFloat(client?.S_Misc_Spend_4) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_5) ||
                parseFloat(client?.S_Misc_Spend_5) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_6) ||
                parseFloat(client?.S_Misc_Spend_6) ||
                0) +
              (parseFloat(clientData?.S_Misc_Spend_7) ||
                parseFloat(client?.S_Misc_Spend_7) ||
                0)
            ).toString()}
          </Tag>
        </Flex>
        <Flex p={3} gap={5}>
          {/* Social */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Social:{" "}
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Social"
                value={clientData.Social || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Social Split New:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Social_Split_New"
                value={clientData.Social_Split_New || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Social Split Used:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Social_Split_Used"
                value={clientData.Social_Split_Used || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Social New:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Social_New"
                value={clientData.Social_New || "0"}
                readOnly
              />
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Social Used:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Social_Used"
                value={clientData.Social_Used || "0"}
                readOnly
              />
            </Flex>
          </Flex>
          {/* Spanish */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Spanish Social:{" "}
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_Social"
                value={clientData.Spanish_Social || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Spanish Social Split New:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Social_Spanish_Split_New"
                value={clientData.Social_Spanish_Split_New || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Spanish Social Split Used:
              </Text>
              <Text>%</Text>
              <Input
                size={"xs"}
                w={"40px"}
                name="Social_Spanish_Split_Used"
                value={clientData.Social_Spanish_Split_Used || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Spanish Social New:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Social_Spanish_New"
                value={
                  clientData.Social_Spanish_New !== undefined
                    ? clientData.Social_Spanish_New
                    : "0"
                }
                readOnly
              />
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Spanish Social Used:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Social_Spanish_Used"
                value={
                  clientData.Social_Spanish_Used !== undefined
                    ? clientData.Social_Spanish_Used
                    : "0"
                }
                readOnly
              />
            </Flex>
          </Flex>
          <Flex direction={"column"} gap={1}>
            {/* Fixed Ops */}
            <Flex direction={"column"} gap={1}>
              <Flex gap={1}>
                <Text color={social} as={"b"}>
                  Fixed Ops:
                </Text>
                <Text>$</Text>
                <Input
                  size={"xs"}
                  w={"60px"}
                  name="Fixed_Ops_Social"
                  value={clientData?.Fixed_Ops_Social || ""}
                  onChange={handleInputChange}
                ></Input>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Flex p={3} gap={5}>
          {/* Misc 1 */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 1:{" "}
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_1"
                value={clientData?.S_Description_1 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_1"
                value={clientData?.S_Misc_Spend_1 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 2:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_2"
                value={clientData?.S_Description_2 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_2"
                value={clientData?.S_Misc_Spend_2 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 3:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_3"
                value={clientData?.S_Description_3 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_3"
                value={clientData?.S_Misc_Spend_3 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 4:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_4"
                value={clientData?.S_Description_4 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_4"
                value={clientData?.S_Misc_Spend_4 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 5:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_5"
                value={clientData?.S_Description_5 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_5"
                value={clientData?.S_Misc_Spend_5 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
          </Flex>
          {/* Misc 2 */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 6:{" "}
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_6"
                value={clientData?.S_Description_6 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_6"
                value={clientData?.S_Misc_Spend_6 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
            <Flex gap={1}>
              <Text color={social} as={"b"}>
                Misc 7:
              </Text>
              <Input
                placeholder="Description"
                size={"xs"}
                w={"300px"}
                name="S_Description_7"
                value={clientData?.S_Description_7 || ""}
                onChange={handleInputChange}
              ></Input>
              <Text>- $</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="S_Misc_Spend_7"
                value={clientData?.S_Misc_Spend_7 || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
          </Flex>
        </Flex>
        <Heading as={"h2"} size={"lg"} py={3}>
          OTT
        </Heading>
        <Flex gap={1} pl={3}>
          <Text color={ott} as={"b"}>
            OTT Total Budget:
          </Text>
          <Tag variant={"solid"} colorScheme="linkedin">
            $
            {clientData.OTT
              ? (
                  (parseFloat(clientData.OTT) || parseFloat(client?.OTT)) +
                  (parseFloat(clientData.Spanish_OTT) ||
                    parseFloat(client?.Spanish_OTT))
                ).toString()
              : 0}
          </Tag>
        </Flex>
        {/* OTT */}
        <Flex p={3} gap={5}>
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={ott} as={"b"}>
                OTT:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="OTT"
                value={clientData?.OTT || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
          </Flex>
          {/* Spanish OTT */}
          <Flex direction={"column"} gap={1}>
            <Flex gap={1}>
              <Text color={ott} as={"b"}>
                Spanish OTT:
              </Text>
              <Text>$</Text>
              <Input
                size={"xs"}
                w={"60px"}
                name="Spanish_OTT"
                value={clientData?.Spanish_OTT || ""}
                onChange={handleInputChange}
              ></Input>
            </Flex>
          </Flex>
        </Flex>
      </Card>
      <ScheduleModal
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        client={client}
      />
    </Flex>
  );
}

export default BudgetEditor;
